import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Check, ChevronRight, Loader2, ArrowLeft } from "lucide-react";
import { getPackages, getTimeSlots, createBookingCheckout } from "../lib/apiClient";
import { VEHICLE_TYPES } from "../data";

const todayPlus = (d) => {
  const t = new Date();
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 10);
};

export default function BookingModal({ open, onOpenChange, presetPackage }) {
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [date, setDate] = useState(todayPlus(1));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) getPackages().then(setPackages).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1); setVehicle(null); setPkg(null); setSlot(null);
        setForm({ name: "", email: "", phone: "", notes: "" });
      }, 250);
    }
  }, [open]);

  useEffect(() => {
    if (presetPackage && packages.length) {
      const p = packages.find((x) => x.id === presetPackage);
      if (p) { setPkg(p); setVehicle(p.vehicles[0]); setStep(3); }
    }
  }, [presetPackage, packages]);

  useEffect(() => {
    if (step === 3 && date) {
      getTimeSlots(date).then((d) => setSlots(d.slots)).catch(() => setSlots([]));
    }
  }, [step, date]);

  const availablePackages = packages.filter((p) => !vehicle || p.vehicles.includes(vehicle));

  const handlePay = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { checkout_url } = await createBookingCheckout({
        package_id: pkg.id, vehicle_type: vehicle, date, time_slot: slot,
        ...form, origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-2xl bg-[#080808] border border-zinc-800 rounded-none p-0 gap-0 max-h-[92vh] overflow-y-auto text-white"
      >
        <DialogTitle className="sr-only">Book a service</DialogTitle>
        <div className="p-7 md:p-9">
          <div className="flex items-center justify-between mb-7">
            <div>
              <p className="text-[11px] tracking-[0.25em] text-[#FF3B30] uppercase">Book a Service</p>
              <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-1">
                Secure your slot
              </h3>
            </div>
            <span className="font-display text-sm text-zinc-500">STEP {step} / 4</span>
          </div>

          {step === 1 && (
            <div>
              <Label className="text-zinc-400 text-xs tracking-widest uppercase">Vehicle type</Label>
              <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mt-3">
                {VEHICLE_TYPES.map((v) => (
                  <button
                    key={v.key}
                    data-testid={`booking-vehicle-${v.key}`}
                    onClick={() => { setVehicle(v.key); setPkg(null); setStep(2); }}
                    className={`bg-[#0A0A0A] py-10 font-display uppercase tracking-wide transition-colors duration-300 hover:bg-[#FF3B30] hover:text-white ${vehicle === v.key ? "text-[#FF3B30]" : "text-white"}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <button onClick={() => setStep(1)} className="flex items-center gap-1 text-zinc-500 text-sm mb-4 hover:text-white transition-colors" data-testid="booking-back-2">
                <ArrowLeft size={14} /> Back
              </button>
              <Label className="text-zinc-400 text-xs tracking-widest uppercase">Choose a package</Label>
              <div className="mt-3 space-y-px bg-zinc-800 border border-zinc-800">
                {availablePackages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`booking-package-${p.id}`}
                    onClick={() => { setPkg(p); setStep(3); }}
                    className="w-full text-left bg-[#0A0A0A] p-5 flex items-center justify-between group hover:bg-[#111] transition-colors"
                  >
                    <div>
                      <p className="font-display text-lg uppercase tracking-tight group-hover:text-[#FF3B30] transition-colors">{p.name}</p>
                      <p className="text-zinc-500 text-sm mt-0.5">{p.tagline}</p>
                      <p className="text-zinc-600 text-xs mt-1">{p.duration} · from ${p.from_price}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <p className="text-[#FF3B30] font-display text-xl">${p.deposit}</p>
                      <p className="text-zinc-600 text-[10px] uppercase tracking-widest">deposit</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && pkg && (
            <div>
              <button onClick={() => setStep(2)} className="flex items-center gap-1 text-zinc-500 text-sm mb-4 hover:text-white transition-colors" data-testid="booking-back-3">
                <ArrowLeft size={14} /> Back
              </button>
              <Label className="text-zinc-400 text-xs tracking-widest uppercase">Pick a date</Label>
              <Input
                type="date" value={date} min={todayPlus(1)}
                data-testid="booking-date"
                onChange={(e) => { setDate(e.target.value); setSlot(null); }}
                className="mt-3 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white"
              />
              <Label className="text-zinc-400 text-xs tracking-widest uppercase mt-6 block">Time slot</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mt-3">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    disabled={!s.available}
                    data-testid={`booking-slot-${s.time}`}
                    onClick={() => setSlot(s.time)}
                    className={`bg-[#0A0A0A] py-5 font-display transition-colors duration-300 disabled:opacity-30 disabled:line-through ${slot === s.time ? "bg-[#FF3B30] text-white" : "hover:bg-[#111]"}`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
              <Button
                disabled={!slot}
                data-testid="booking-to-details"
                onClick={() => setStep(4)}
                className="w-full mt-7 rounded-none bg-white text-black hover:bg-zinc-200 h-12 font-display uppercase tracking-widest disabled:opacity-40"
              >
                Continue <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          )}

          {step === 4 && pkg && (
            <div>
              <button onClick={() => setStep(3)} className="flex items-center gap-1 text-zinc-500 text-sm mb-4 hover:text-white transition-colors" data-testid="booking-back-4">
                <ArrowLeft size={14} /> Back
              </button>
              <div className="border border-zinc-800 bg-[#0A0A0A] p-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">{pkg.name} · {vehicle}</span>
                  <span className="text-white">{date} @ {slot}</span>
                </div>
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Deposit due now</span>
                  <span className="text-[#FF3B30] font-display text-lg">${pkg.deposit} AUD</span>
                </div>
                <p className="text-zinc-600 text-xs mt-2">Balance (${pkg.from_price - pkg.deposit}+) settled in person on the day.</p>
              </div>
              <div className="grid gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs tracking-widest uppercase">Full name</Label>
                  <Input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs tracking-widest uppercase">Email</Label>
                    <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs tracking-widest uppercase">Phone</Label>
                    <Input data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs tracking-widest uppercase">Notes (optional)</Label>
                  <Textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Vehicle make/model, condition, anything we should know…" className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white resize-none" />
                </div>
              </div>
              <Button
                onClick={handlePay}
                disabled={submitting}
                data-testid="booking-pay-deposit"
                className="w-full mt-7 rounded-none bg-[#FF3B30] hover:bg-[#D63026] text-white h-13 py-4 font-display uppercase tracking-widest"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : <>Pay ${pkg.deposit} deposit <Check size={16} className="ml-2" /></>}
              </Button>
              <p className="text-center text-zinc-600 text-xs mt-3">Secure payment via Stripe · test card 4242 4242 4242 4242</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
